import React from 'react';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost' | 'success' | 'warning' | 'info' | 'purple';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

/**
 * Button component following Oler Industrial SCADA styling
 * 
 * Features heavy borders, glows for status indication, and uppercase text
 */
export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  isLoading = false, 
  leftIcon, 
  rightIcon, 
  className = '', 
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  ...props 
}) => {
  
  // Base styles following Oler industrial aesthetic
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    font-bold uppercase tracking-wider
    transition-all duration-fast
    focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-[var(--app-bg)]
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-[0.98]
  `;
  
  // Variant styles with glows and industrial colors
  // Note: Primary/danger/success/warning/info/purple buttons keep white text for contrast on colored backgrounds
  const variants = {
    primary: `
      border border-oler-blue/50 
      bg-oler-blue/20 
      text-oler-blue-light 
      shadow-glow-primary
      hover:bg-oler-blue/30 hover:border-oler-blue/60
      focus:ring-oler-blue
    `,
    secondary: `
      border border-[color:var(--border)] 
      bg-[var(--surface-bg)] 
      text-[color:var(--text)] 
      hover:bg-[var(--bezel-bg)]
      focus:ring-oler-blue
    `,
    danger: `
      border border-danger/40 
      bg-danger/15 
      text-danger 
      shadow-glow-danger
      hover:bg-danger/25 hover:border-danger/50
      focus:ring-danger
    `,
    success: `
      border border-success/40 
      bg-success/15 
      text-success 
      shadow-glow-success
      hover:bg-success/25 hover:border-success/50
      focus:ring-success
    `,
    warning: `
      border border-warning/40 
      bg-warning/15 
      text-warning 
      shadow-glow-warning
      hover:bg-warning/25 hover:border-warning/50
      focus:ring-warning
    `,
    info: `
      border border-info/40 
      bg-info/15 
      text-info 
      shadow-glow-accent
      hover:bg-info/25 hover:border-info/50
      focus:ring-info
    `,
    purple: `
      border border-purple-500/40 
      bg-purple-500/15 
      text-purple-500 
      shadow-[0_0_0_1px_rgba(168,85,247,0.28),0_0_24px_rgba(168,85,247,0.18)]
      hover:bg-purple-500/25 hover:border-purple-500/50
      focus:ring-purple-500
    `,
    outline: `
      border border-[color:var(--border)] 
      bg-transparent 
      text-sub 
      hover:bg-[var(--surface-bg)] hover:text-[color:var(--text)]
      focus:ring-oler-blue
    `,
    ghost: `
      border border-transparent 
      bg-transparent 
      text-sub 
      hover:bg-[var(--surface-bg)] hover:text-[color:var(--text)]
      focus:ring-oler-blue
    `,
  };

  // Size styles - all sizes have consistent heights within their tier
  const sizes = {
    sm: "text-[11px] px-3 h-8 rounded-md",
    md: "text-xs px-4 h-9 rounded-lg",
    lg: "text-sm px-6 h-11 rounded-xl",
    icon: "h-8 w-8 rounded-lg",
  };

  // Auto-generate aria-label for icon-only buttons if not provided
  const finalAriaLabel = ariaLabel || (size === 'icon' && !children ? 'Button' : undefined);

  return (
    <button 
      type={type}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      aria-label={finalAriaLabel}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
      {!isLoading && leftIcon && <span aria-hidden="true">{leftIcon}</span>}
      {children}
      {!isLoading && rightIcon && <span aria-hidden="true">{rightIcon}</span>}
    </button>
  );
};
